import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
    KnowledgeDocument,
    KnowledgeDocumentsApiService,
    KnowledgeDocumentsResponse,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, getFormattedDateOrNull, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest, EMPTY, mergeMap, Subscription} from 'rxjs';
import {fieldExtractor, FilterDictionaryField, FilterEvent, FilterField} from '../../../components/full-table/filter/filter.component';
import {initialTableEvent, TableEvent} from '../../../components/full-table/full-table.component';
import {INITIAL_PAGE_NUMBER, INITIAL_PAGE_SIZE} from '../../../components/full-table/paging/paginator.component';
import {Column, ColumnType, CustomLayoutColumn, CustomLayoutItem, Row} from '../../../components/full-table/table/table.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../domain/dictionary-holder.service';
import {KNOWLEDGE_DOCUMENT_ID} from '../../../domain/query-params.keys';
import {Path} from '../../../path';

@Component({
    selector: 'af-knowledge-base-documents-tab',
    standalone: false,
    templateUrl: './knowledge-base-documents-tab.component.html',
})
export class KnowledgeBaseDocumentsTabComponent implements OnInit, OnDestroy {
    @Input() mode: 'non_archive' | 'archive' = 'non_archive';
    @Output() hasDataListener = new EventEmitter<boolean>();
    @Output() addDocumentClickListener = new EventEmitter<void>();

    isAdmin = this.authHolderService.hasAccess(['npck-admin']);

    filterFields: FilterField[] = [
        {name: 'query', type: 'text'},
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'type',
            translationPrefix: 'knowledgeBase',
            dictionaryName: ReferenceBookType.KNOWLEDGE_BASE_DOCS,
        },
        {name: 'dateRange', type: 'dateRange'},
    ];

    tableEvent: TableEvent = {
        ...initialTableEvent,
        spinnerId: 'knowledgeDocuments',
        filter: {query: ''},
    };
    dataSource: Row[] = [];
    totalElements: number;
    private loadSubscription: Subscription;

    constructor(
        private knowledgeDocumentsApiService: KnowledgeDocumentsApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
    ) {
    }

    ngOnInit(): void {
        if (this.isAdmin) {
            this.loadDocuments();
        }
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
    }

    loadDocuments(tableEvent?: TableEvent): void {
        if (tableEvent) {
            this.tableEvent = tableEvent;
        }
        this.loadSubscription?.unsubscribe();
        if (this.tableEvent?.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }
        const filterEvent = tableEvent?.filter;
        this.loadSubscription = combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.KNOWLEDGE_BASE_DOCS),
            this.knowledgeDocumentsApiService.getKnowledgeDocuments({
                isArchived: this.mode === 'archive',
                name: filterEvent?.query,
                types: fieldExtractor(filterEvent?.type),
                dateFrom: getFormattedDateOrNull(filterEvent?.dateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                dateTo: getFormattedDateOrNull(filterEvent?.dateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
                limit: tableEvent?.page?.pageSize,
                offset: tableEvent?.page?.pageOffset,
                sortBy: tableEvent?.sortBy?.columnName,
                sortOrder: tableEvent?.sortBy?.direction,
            }),
        ])
            .pipe(rxSpinner(tableEvent?.spinnerId ?? 'initial'))
            .subscribe(([dictionary, response]) => this.buildDataSource(response, dictionary));
    }

    private buildDataSource(response: KnowledgeDocumentsResponse, dictionary: ReferenceBookResponse[]): void {
        this.totalElements = response.total;
        const filterEvent = this.tableEvent?.filter;
        this.hasDataListener.emit(response.total > 0 || this.hasActiveFilter(filterEvent));
        this.dataSource = (response.items ?? []).map(item => <Row>{
            actions: this.buildActions(item),
            name: <Column>{
                type: ColumnType.TEXT,
                text: item.name,
                cellStyle: {ellipsisTextLarge: true},
                sortable: true
            },
            type: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(dictionary, item.type, 'name_ru'),
                sortable: true
            },
            createdDate: <Column>{
                type: ColumnType.DATETIME,
                text: item.createdDate,
                sortable: true
            },
        });
    }

    private buildActions(item: KnowledgeDocument): CustomLayoutColumn {
        const actions: Array<CustomLayoutItem> = [];
        if (this.mode === 'non_archive') {
            actions.push({
                    type: 'icon', text: 'ic_edit',
                    click: () => this.routerService.navigate(
                        Path.KNOWLEDGE_BASE_DOC_EDIT, {pathParams: {[KNOWLEDGE_DOCUMENT_ID]: item.id!}},
                    ),
                    tooltip: 'i18n_af_labelEdit', iconSize: 'i-16',
                },
                {
                    type: 'icon', text: 'ic_archive', click: () => this.archiveDocument(item),
                    tooltip: 'i18n_af_buttonArchive', iconSize: 'i-16',
                },
            );
        } else {
            actions.push({
                type: 'icon', text: 'ic_unarchive', click: () => this.unarchiveDocument(item),
                tooltip: 'i18n_af_buttonUnarchive', iconSize: 'i-16',
            });
        }

        actions.push({
            type: 'icon', text: 'ic_delete', click: () => this.deleteDocument(item),
            tooltip: 'i18n_af_labelDelete', iconSize: 'i-16',
        });
        return <CustomLayoutColumn>{
            type: ColumnType.CUSTOM_LAYOUT,
            cellStyle: {layoutDirection: 'row', layoutAlignFirst: 'start', layoutAlignSecond: 'center', layoutGap: '8px'},
            layoutContentItems: actions,
        };
    }

    private archiveDocument(item: KnowledgeDocument): void {
        this.dialogService.openDialog({
            title: 'i18n_af_archiveDialogTitle',
            subtitle: 'i18n_af_archiveDialogSubtitle',
            buttonOkText: 'i18n_af_buttonArchive',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {name: item.name},
        }).pipe(
            mergeMap(result => !result ? EMPTY :
                this.knowledgeDocumentsApiService.updateKnowledgeDocument(item.id!, {isArchived: true})
                    .pipe(rxSpinner('archiving'))),
        ).subscribe(() => {
            this.snackbarService.showSnackBar('i18n_af_snackBarDocumentArchived');
            this.reloadTable();
        });
    }

    private unarchiveDocument(item: KnowledgeDocument): void {
        this.dialogService.openDialog({
            title: 'i18n_af_unarchiveDialogTitle',
            subtitle: 'i18n_af_unarchiveDialogSubtitle',
            buttonOkText: 'i18n_af_buttonUnarchive',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {name: item.name},
        }).pipe(
            mergeMap(result => !result ? EMPTY :
                this.knowledgeDocumentsApiService.updateKnowledgeDocument(item.id!, {isArchived: false})
                    .pipe(rxSpinner('archiving'))),
        ).subscribe(() => {
            this.snackbarService.showSnackBar('i18n_af_snackBarDocumentUnarchived');
            this.reloadTable();
        });
    }

    private deleteDocument(item: KnowledgeDocument): void {
        this.dialogService.openDialog({
            title: 'i18n_af_deleteFileDialogTitle',
            subtitle: 'i18n_af_deleteFileDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDeleteFile',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {name: item.name},
        }).pipe(
            mergeMap(result => !result ? EMPTY :
                this.knowledgeDocumentsApiService.deleteKnowledgeDocument(item.id!).pipe(rxSpinner('deleting'))),
        ).subscribe(() => {
            this.snackbarService.showSnackBar('i18n_af_snackBarDocumentDeleted');
            this.reloadTable();
        });
    }

    private reloadTable(): void {
        this.loadDocuments({
            page: {pageSize: INITIAL_PAGE_SIZE, pageNumber: INITIAL_PAGE_NUMBER, pageOffset: INITIAL_PAGE_NUMBER * INITIAL_PAGE_SIZE},
            spinnerId: 'initial',
        });
    }

    private hasActiveFilter(filterEvent: FilterEvent): boolean {
        return !!filterEvent?.query?.trim()
            || !!fieldExtractor(filterEvent?.type)?.length
            || !!(filterEvent?.dateRange?.dateFrom || filterEvent?.dateRange?.dateTo);
    }
}
